import React, { useState } from 'react';
import { AlertTriangleIcon, ShieldIcon, AlertCircleIcon, XCircleIcon, SirenIcon } from 'lucide-react';
const EmergencyProtocol = () => {
  const [selectedLevel, setSelectedLevel] = useState<number | null>(null);
  const emergencyLevels = [{
    level: 1,
    title: 'Resistencia Pasiva',
    icon: <ShieldIcon size={24} className="text-yellow-500" data-id="element-732" />,
    description: 'Él responde, pero de forma corta y sin entusiasmo. Tarda en responder o parece distante.',
    symptoms: ['Respuestas cortas y sin compromiso', 'Demora en responder los mensajes', 'Conversaciones superficiales sin profundidad', 'Falta de iniciativa para mantener contacto'],
    protocol: ['Aplica el Protocolo de Escasez por 3-5 días (deja de iniciar contacto)', 'Después del período, envía un Disparador de Valor o Nostalgia', 'Mantén conversaciones ligeras y sin presión emocional', 'Enfócate en tu crecimiento personal y compártelo indirectamente']
  }, {
    level: 2,
    title: 'Resistencia Activa',
    icon: <AlertCircleIcon size={24} className="text-orange-500" data-id="element-733" />,
    description: 'Él demuestra irritación, cuestionamientos o resistencia clara a tus intentos de acercamiento.',
    symptoms: ['Respuestas defensivas o irritadas', 'Cuestionamientos sobre tus intenciones', 'Menciones al pasado de forma negativa', 'Comparaciones con otras personas'],
    protocol: ['Implementa el Retroceso Estratégico por 7 días completos (silencio total)', 'Regresa con un Disparador de Vulnerabilidad sincero', 'Reconoce los errores del pasado sin autodesvalorización', 'Demuestra crecimiento personal concreto', 'Mantén contacto espaciado (máximo 1 mensaje cada 2 días)']
  }, {
    level: 3,
    title: 'Bloqueo Total',
    icon: <XCircleIcon size={24} className="text-red-500" data-id="element-734" />,
    description: 'Él te bloqueó en redes sociales o no responde de ninguna manera a tus mensajes.',
    symptoms: ['Bloqueo en una o más redes sociales', 'Mensajes vistos sin respuesta', 'Mensajes no entregados o no vistos', 'Pedidos directos para no hacer contacto'],
    protocol: ['Activa el Protocolo de Silencio por 21-30 días', 'Enfócate intensamente en transformación personal visible', 'Crea contacto indirecto a través de amigos comunes', 'Después del período, envía un único mensaje de Cierre Emocional', 'Prepárate para el Reencuentro Estratégico en ambiente neutro']
  }, {
    level: 4,
    title: 'Hostilidad Extrema',
    icon: <SirenIcon size={24} className="text-red-700" data-id="element-735" />,
    description: 'Él demuestra ira intensa, amenazas o comportamiento agresivo en respuesta a tus contactos.',
    symptoms: ['Mensajes agresivos o amenazadores', 'Involucra a terceros para alejarte', 'Difamación o exposición pública', 'Comportamiento impredecible o peligroso'],
    protocol: ['Interrumpe todo contacto inmediatamente', 'Documenta cualquier comportamiento preocupante', 'Busca apoyo profesional (terapeuta/coach)', 'Reevalúa si la reconquista es saludable en este momento', 'Considera el Protocolo de Finalización Definitiva', 'Enfócate en tu seguridad emocional y física']
  }];
  return <div className="max-w-4xl mx-auto" data-id="element-736">
      <header className="mb-8 text-center" data-id="element-737">
        <h1 className="text-3xl font-bold text-[#2C2C2C] mb-2" data-id="element-738">
          Protocolo de Emergencia
        </h1>
        <p className="text-gray-600" data-id="element-739">
          Estrategias para situaciones difíciles y resistencia
        </p>
      </header>
      <div className="bg-white rounded-xl shadow-md p-6 mb-8" data-id="element-740">
        <div className="flex items-center mb-6" data-id="element-741">
          <AlertTriangleIcon size={20} className="text-red-500 mr-2" data-id="element-742" />
          <h2 className="text-xl font-semibold text-[#2C2C2C]" data-id="element-743">
            Identifica el Nivel de Resistencia
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6" data-id="element-744">
          {emergencyLevels.map(level => <div key={level.level} className={`border rounded-lg p-4 cursor-pointer transition-all ${selectedLevel === level.level ? 'border-red-500 bg-red-50' : 'border-gray-200 hover:border-gray-300'}`} onClick={() => setSelectedLevel(level.level)} data-id="element-745">
              <div className="flex items-center mb-3" data-id="element-746">
                <div className="p-2 rounded-full bg-gray-100 mr-3" data-id="element-747">
                  {level.icon}
                </div>
                <div data-id="element-748">
                  <h3 className="font-bold" data-id="element-749">
                    Nivel {level.level}: {level.title}
                  </h3>
                  <p className="text-sm text-gray-600" data-id="element-750">{level.description}</p>
                </div>
              </div>
            </div>)}
        </div>
      </div>
      {selectedLevel !== null && <div className="bg-white rounded-xl shadow-md p-6" data-id="element-751">
          <h2 className="text-xl font-semibold text-[#2C2C2C] mb-4" data-id="element-752">
            Protocolo para Nivel {selectedLevel}:{' '}
            {emergencyLevels[selectedLevel - 1].title}
          </h2>
          <div className="mb-6" data-id="element-753">
            <h3 className="font-medium text-red-600 mb-2" data-id="element-754">Síntomas:</h3>
            <ul className="list-disc pl-5 space-y-1" data-id="element-755">
              {emergencyLevels[selectedLevel - 1].symptoms.map((symptom, index) => <li key={index} className="text-gray-700" data-id="element-756">
                    {symptom}
                  </li>)}
            </ul>
          </div>
          <div className="mb-6" data-id="element-757">
            <h3 className="font-medium text-[#9C27B0] mb-2" data-id="element-758">
              Protocolo de Acción:
            </h3>
            <ol className="list-decimal pl-5 space-y-3" data-id="element-759">
              {emergencyLevels[selectedLevel - 1].protocol.map((step, index) => <li key={index} className="text-gray-700" data-id="element-760">
                    {step}
                  </li>)}
            </ol>
          </div>
          <div className="bg-red-50 border-l-4 border-red-500 p-4" data-id="element-761">
            <div className="flex" data-id="element-762">
              <AlertTriangleIcon size={20} className="text-red-500 mr-2 flex-shrink-0 mt-0.5" data-id="element-763" />
              <div data-id="element-764">
                <h4 className="font-bold text-red-700" data-id="element-765">Importante:</h4>
                <p className="text-gray-700" data-id="element-766">
                  Si en algún momento sientes que la situación está afectando
                  negativamente tu salud mental o si hay cualquier
                  comportamiento abusivo, prioriza tu bienestar y considera
                  pausar el protocolo de reconquista. Ninguna relación vale el
                  sacrificio de tu paz mental y dignidad.
                </p>
              </div>
            </div>
          </div>
        </div>}
    </div>;
};
export default EmergencyProtocol;